// Stats panel body — min/avg/max for the same numeric series LineChartBody
// charts. Reads /api/features/timeseries (decimal-only rows) so the
// numbers match what the line chart shows.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';

interface TimeSeriesDataPoint { date: number; value: number; }

const StatsPanelBody: React.FC<ChartBodyProps> = ({ featureId, config, fullscreen }) => {
  const [rawData, setRawData] = useState<TimeSeriesDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`/api/features/timeseries/${featureId}`)
      .then((response) => {
        if (cancelled) return;
        const processed = response.data.map((item: any) => ({
          ...item,
          date: new Date(item.date).getTime(),
        }));
        setRawData(processed);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch time series data:', err);
        if (!cancelled) setError('Failed to load chart data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rawData.map((r) => r.date), [rawData]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );
  const data = useMemo(
    () => rawData.filter((d) => withinWindow(d.date, win)),
    [rawData, win],
  );

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rawData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No numeric data available for this feature.</p>
        <p className="text-sm">Add check-ins with numeric values to see stats.</p>
      </div>
    );
  }

  const formatStatValue = (value: number) => {
    if (config?.type === 'labeled_rating' && config.params?.labels && config.params.labels[Math.round(value) - 1]) {
      return config.params.labels[Math.round(value) - 1];
    }
    return value > 0 && value < 0.1 ? '<0.1' : value.toFixed(1);
  };

  const values = data.map((d) => d.value);
  const hasData = values.length > 0;
  const sum = hasData ? values.reduce((acc, val) => acc + val, 0) : 0;
  const avg = hasData ? sum / values.length : 0;
  const min = hasData ? Math.min(...values) : 0;
  const max = hasData ? Math.max(...values) : 0;

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : ''}>
      <ChartTimeRangePicker state={range} />
      <div className={`flex items-center justify-center ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
        {hasData ? (
          <div className="text-center space-y-6 max-w-4xl mx-auto w-full px-2 py-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="bg-amber-50 p-5 rounded-xl border border-amber-100 flex-1 min-w-0">
                <div className="text-amber-800 text-xs font-medium uppercase tracking-wide mb-1">Minimum</div>
                <div className="text-2xl font-bold text-amber-800 truncate">{formatStatValue(min)}</div>
              </div>
              <div className="bg-amber-100 p-5 rounded-xl border border-amber-200 flex-1 min-w-0">
                <div className="text-amber-800 text-xs font-medium uppercase tracking-wide mb-1">Average</div>
                <div className="text-2xl font-bold text-amber-900 truncate">{formatStatValue(avg)}</div>
              </div>
              <div className="bg-amber-200 p-5 rounded-xl border border-amber-300 flex-1 min-w-0">
                <div className="text-amber-900 text-xs font-medium uppercase tracking-wide mb-1">Maximum</div>
                <div className="text-2xl font-bold text-amber-900 truncate">{formatStatValue(max)}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Based on {data.length} data point{data.length !== 1 ? 's' : ''}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No values in this period.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPanelBody;
